import { Grid, LinearProgress, styled, TextField, Typography } from '@mui/material';
import { FileWithProgress } from '@oh-vcp/components-common';
import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import CaseService from '../services/caseService';

interface EconsultAttachmentFileItemProps {
  file: FileWithProgress;
  onDelete?: (uid: string | undefined, fileId: string | undefined) => void;
  onDescriptionChanged?: (
    uid: string | undefined,
    fileDescription: string,
    fileId: string | number | undefined
  ) => void;
  disableDelete?: boolean;
}

const FileProgressBar = styled(LinearProgress)({
  height: 10,
  marginTop: 6,
  width: '100%',
  marginLeft: 20
});

const DescriptionField = styled(TextField)({
  width: '66%',
  marginTop: -8,
  '& .MuiInputBase-input': {
    fontSize: 14
  },
  '& .MuiInputLabel-root': {
    fontSize: 14
  }
});

const EconsultAttachmentFileItem: FC<EconsultAttachmentFileItemProps> = ({
  file,
  onDelete,
  onDescriptionChanged,
  disableDelete
}) => {
  const { t } = useTranslation();
  const handleDelete = (uid: string | undefined, fileId: string | undefined) => {
    if (onDelete) {
      onDelete(uid, fileId);
    }
  };
  const handleDescriptionChange = (
    uid: string | undefined,
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fileId: string | number | undefined
  ) => {
    if (onDescriptionChanged) {
      onDescriptionChanged(uid, e.target.value, fileId);
    }
  };
  const downloadAttachment = async (fileId: string | undefined, fileName: string | undefined) => {
    if (!fileId || !fileName) return;
    const fileUrl = await CaseService.getFileUrl(fileId);
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  const progressVisible = file.progress > 0 && file.progress < 100 ? 'block' : 'none';
  return (
    <Grid container>
      <Grid item xs={8} md={7} sx={{ textAlign: 'left' }}>
        <Typography
          variant="body2"
          color={file.progress < 100 ? 'base.grey4' : 'primary.main'}
          sx={{ cursor: 'pointer' }}
          onClick={() => downloadAttachment(file.fileId?.toString(), file.file.name)}>
          {file.file.name}
        </Typography>
      </Grid>
      <Grid item xs={8} md={4} sx={{ textAlign: 'left', display: progressVisible }}>
        {file.progress > 0 && file.progress < 100 && (
          <FileProgressBar variant="determinate" value={file.progress} />
        )}
      </Grid>
      {file.uploadFailed && (
        <Typography variant="body4" color="#EA4335">
          {t('Case.attachment.uploadFailed')}
        </Typography>
      )}
      {!disableDelete && (
        <Grid item xs={3} md={1} sx={{ textAlign: 'right' }}>
          <Typography
            variant="body1"
            color="base.grey4"
            sx={{ cursor: 'pointer' }}
            onClick={() => handleDelete(file.uid, file.fileId?.toString())}>
            X
          </Typography>
        </Grid>
      )}
      {!file.uploadFailed && (
        <Grid xs={12} md={12} sx={{ textAlign: 'left' }}>
          <DescriptionField
            onBlur={(e) => handleDescriptionChange(file.uid, e, file.fileId)}
            defaultValue={file.fileDescription}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              inputProps: {
                maxLength: 260
              }
            }}
            placeholder={t('Case.attachment.describeTheFile') as string}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default EconsultAttachmentFileItem;
